import { Card, Col, Form, Row } from 'antd';
import React, { useMemo, useState } from 'react';
import { Loadable } from '../components/loadable/Loadable';
import SelectOrTransfer from '../components/selectOrTransfer/SelectOrTransfer';
import { FeatureFlag, useFeatureFlags } from '../featureFlags';
import { flags } from '../featureFlags/flags';
import { useBrandsQuery, useGroupListQuery, useLocalesQuery, useSyncObjectRelationshipsMutation } from '../gql/hooks';
import { BrandUtil } from '../gql/utils';
import { GroupUtil } from '../gql/utils/GroupUtil';
import { authService } from '../auth/authService';
import RosterGroups from '../updated/components/RosterGroups/RosterGroups';

export const useSegmentation = ( formController, { updatedBrandsLocation, formNamePrefix = undefined, defaultSelectBrand = true } = {} ) => {
    const [ updateBrandsLocation ] = useState( updatedBrandsLocation || false );

    const [ syncRelationships, { loading: isSyncLoading, error: syncRelationshipsError } ] = useSyncObjectRelationshipsMutation();

    const [ brandsAllowed, companyRelSegmentation, productsAllowed, learningProfilesAllowed, brandsUserRelationshipRequired, 
        localesAllowed, groupSegmantationPayload, segmentationAllowed, rosterEditEnabled ]
        = useFeatureFlags( [ flags.BRANDS, flags.SEGMENTATION_COMPANY_RELATIONSHIP, 
            flags.SEGMENTATION_PRODUCT, flags.SEGMENTATION_LEARNING_PROFILES, flags.BRANDS_USER_RELATIONSHIP_REQUIRED,
            flags.SEGMENTATION_LOCALES, flags.SEGMENTATION_CLIENT_MANAGED_ATTRIBUTES_ENABLED, flags.SEGMENTATION_CLIENT_MANAGED,
            flags.ROSTER_EDIT_ENABLED ] );

    const { data: allLocales, loading: localeLoading } = useLocalesQuery( { fetchPolicy: 'network-only' } );
 
    /** BRAND MANAGEMENT */
    const { data: brandsResponse, loading: brandsLoading } = useBrandsQuery( { fetchPolicy: 'network-only' } );
    const [ selectedBrands, setSelectedBrands ] = useState( [] );
    const brandOptions = useMemo( () => {
        const brandList = brandsResponse?.brands?.edges || [];
        return brandList.map( ( brand ) => { 
            return {
                key: BrandUtil.getId( brand ),
                label: BrandUtil.getName( brand ),
            };
        } ).sort( i => i.label === 'Hoya' ? -1 : 1 );
    }, [ brandsResponse ] );

    const handleBrandChange = ( brandIds ) => {
        setSelectedBrands( brandIds );
    };

    /** GROUP MANAGEMENT */
    
    const [ segmentIds, setSegmentIds ] = useState( formController.getFieldValue( 'segmentIds' ) ?? [] );
    const { data: allGroups, loading: groupsLoading } = useGroupListQuery( { fetchPolicy: 'network-only' } );
    const segmentGroups = useMemo( () => {
        return ( allGroups?.groups?.edges ? [ ...allGroups.groups.edges ] : [] ).sort( ( a, b ) => {
            return GroupUtil.getName( a )?.localeCompare( GroupUtil.getName( b ) );
        } );
    }, [ allGroups ] );

    const segmentOptions = useMemo( () => {
        const groupList = GroupUtil.filterListByAttribute( segmentGroups, 'locale' );

        return groupList.map( ( group ) => { 
            // The locale groups don't have a nice label representing the locale/language, only the code,
            // so we have to reference the list of locales to get better labels.
            const groupLocaleCode = GroupUtil.getName( group );
            const localeEdges = ( allLocales?.locales?.edges ?? [] );
            const localeLabel = localeEdges.find( ( { node } ) => node.code === groupLocaleCode )?.node?.labelEnUs || groupLocaleCode;
           
            return {
                key: GroupUtil.getId( group ),
                label: `${ localeLabel } (${ groupLocaleCode })`
            };
        } ).sort( ( optionA, optionB ) => {
            const labelOptionA = ( optionA?.label?.toLowerCase() || '' );
            const labelOptionB = ( optionB?.label?.toLowerCase() || '' );
            return labelOptionA.localeCompare( labelOptionB );
        } );
    }, [ segmentGroups, allLocales ] );

    /** COMPANY RELATIONSHIP MANAGEMENT */
    const relationshipToCompanyOptions = useMemo( () => {
        // Retrieve all groups that are available to act as segments representing the user's
        // relationship to the company.
        const groupList = GroupUtil.filterListByAttribute( segmentGroups, 'company-relationship' );
        return groupList.map( ( group ) => ( {
            key: GroupUtil.getId( group ),
            label: GroupUtil.getName( group )
        } ) );
    }, [ segmentGroups ] );

    /** LEARNING PROFILE MANAGEMENT */
    const learningProfileOptions = useMemo( () => {
        // Retrieve all groups that are available to act as segments representing the user's
        // learning profiles.

        const groupList = GroupUtil.filterListByAttribute( segmentGroups, 'lms-learning-profile' );

        return groupList
            .map( ( group ) => ( {
                key: GroupUtil.getId( group ),
                label: GroupUtil.getName( group )
            } ) );
    }, [ segmentGroups ] );

    /** PRODUCT MANAGEMENT */
    const productOptions = useMemo( () => {
        // Retrieve all groups that are available to act as segments representing the user's
        // relationship to products.
        const groupList = GroupUtil.filterListByAttribute( segmentGroups, 'product' );
        return groupList.map( ( group ) => ( {
            key: GroupUtil.getId( group ),
            label: GroupUtil.getName( group )
        } ) );
    }, [ segmentGroups ] );

    const loading = useMemo( () => isSyncLoading || localeLoading || brandsLoading || groupsLoading, [ isSyncLoading, localeLoading, brandsLoading, groupsLoading ] );

    function getFormName( name ) {
        if ( formNamePrefix === undefined ) {
            return name;
        }
        return [ ...formNamePrefix, name ];
    }

    const initialFormValues = {
        segmentIds: [],
        localeIds: [],
        employeeLocaleIds: [],
        companyRelationshipIds: [],
        productIds: [],
        learningProfileIds: [],
    };

    /**
     * Modifies initialFormValues object to include segmentation properties
     * @param {object} init initialFormValues object
     * @returns original initialFormValues object with segmentation options
     */
    function addInitialValues( init ) {
        if ( !init ) {return initialFormValues;}

        return {
            ...init,
            ...initialFormValues,
        };
    }

    /**
     * Pull segmentation information from learning object to present in components
     * Use immediately after fetching learning object
     * @param {Course | Resource | Curriculum} learningObject learning item to pull segmentation from
     */
    function initializeSegmentation( learningObject ) {
        const dataObject = learningObject.node ?? learningObject;

        /** 
         * BRANDS INITIALIZATION
         * Attempt to set the course's attachment to a brand. This will either be what is
         * currently selected or it will try to fallback to the first option as a default.
         * This is assuming brand attachment is allowed.
         */
        let brandsToSelect = null;
        if ( brandsAllowed ) {
            if ( brandsUserRelationshipRequired ) {
                let attachedSegmentIds = dataObject.brandIds;
                const isAttachedToSegment = ( attachedSegmentIds && attachedSegmentIds?.length > 0 );
                if ( !isAttachedToSegment && brandOptions && brandOptions.length > 0 && defaultSelectBrand ) {
                    attachedSegmentIds = [ brandOptions[ 0 ].key ];
                }

                brandsToSelect = attachedSegmentIds;
            } else {
                brandsToSelect = dataObject.brandIds || [];
            }
        }

        /**
         * COMPANY RELATIONSHIP INITIALIZATION
         * Attempt to set the selection of the course's expected user relationship to company.
         * This will either be what is currently selected or it will try to fallback to the first
         * option as a default. This is assuming user relationships are allowed to be defined.
         */
        let companyRelationshipsToSelect = [];
        if ( companyRelSegmentation ) {
            if ( brandsUserRelationshipRequired ) {
                let attachedSegmentIds = dataObject.companyRelationshipIds;
                const isAttachedToSegment = ( attachedSegmentIds && attachedSegmentIds?.length > 0 );
                if ( isAttachedToSegment ) {
                    companyRelationshipsToSelect = attachedSegmentIds;
                } else if ( relationshipToCompanyOptions && relationshipToCompanyOptions.length > 0 ) {
                    // Default to *all* options selected and active. The user will have to modify it
                    // to make the content available to only certain groups.
                    companyRelationshipsToSelect = relationshipToCompanyOptions.map( option => option?.key );
                }
            } else {
                companyRelationshipsToSelect = dataObject.companyRelationshipIds || [];
            }
        }

        /**
         * PRODUCT INITIALIZATION
         * Attempt to set the selection of the course's expected products.
         * This is assuming products are allowed to be defined.
         */
        let productsToSelect = [];
        if ( productsAllowed ) {
            let attachedSegmentIds = dataObject.productIds;
            const isAttachedToSegment = ( attachedSegmentIds && attachedSegmentIds?.length > 0 );
            if ( isAttachedToSegment ) {
                productsToSelect = attachedSegmentIds;
            } else {
                productsToSelect = [];
            }
        }

        /**
         * LEARNING PROFILE INITIALIZATION
         * Attempt to set the selection of the course's expected learning profiles.
         * This is assuming learning profiles are allowed to be defined.
         */
        let lpsToSelect = [];
        if ( learningProfilesAllowed ) {
            let attachedLearningProfiles = dataObject.learningProfileIds;
            const isAttachedToLearningProfile = ( attachedLearningProfiles && attachedLearningProfiles?.length > 0 );
            if ( isAttachedToLearningProfile ) {
                lpsToSelect = attachedLearningProfiles;
            } else {
                lpsToSelect = [];
            }
        }
 
        formController.setFields( [  
            { name: 'brandIds', value: brandsToSelect },
            { name: 'companyRelationshipIds', value: companyRelationshipsToSelect },
            { name: 'productIds', value: productsToSelect },
            { name: 'learningProfileIds', value: lpsToSelect },
            { name: 'segmentIds', value: dataObject.segmentIds },
            { name: 'localeIds', value: dataObject.localeIds },
            { name: 'employeeLocaleIds', value: dataObject.employeeLocaleIds },
        ] );

        setSelectedBrands( brandsToSelect );
        setSegmentIds( dataObject.segmentIds );
    }

    /**
     * Saves the segmentation properties of learning object on form submission
     * Use immediately after saving the learning object on the backend
     * @param {Form} formValues form values object on submit
     * @param {string} learningObjectId UUID identifyinig course, resource, or curriculum
     * @param {string} objectType one of ['course','resource','curriculum']
     */
     
    async function saveSegmentation( formValues, learningObjectId, objectType ) {
        const results = await Promise.all( [
            // Update the attached brands.
            brandsAllowed ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'brands',
                    relatedResourceIds: formValues[ 'brandIds' ] || [],
                } } } ) : Promise.resolve(),

            // Update the attached segments.
            segmentationAllowed ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'segments',
                    relatedResourceIds: formValues[ 'segmentIds' ] || []
                } } } ) : Promise.resolve(),

            // Update the attached locales.
            localesAllowed ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'locales',
                    relatedResourceIds: formValues[ 'localeIds' ] || []
                } } } ) : Promise.resolve(),
            
            // Update the employee locales
            authService.isHoya() ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'employee-locales',
                    relatedResourceIds: formValues[ 'employeeLocaleIds' ] || []
                } } } ) : Promise.resolve(),

            // Update the attached required user relationship to company.
            companyRelSegmentation ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'company-relationships',
                    relatedResourceIds: formValues[ 'companyRelationshipIds' ] || []
                } } } ) : Promise.resolve(),

            // Update the attached products.
            productsAllowed ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'products',
                    relatedResourceIds: formValues[ 'productIds' ] || []
                } } } ) : Promise.resolve(),

            // Update the attached learning profiles.
            learningProfilesAllowed ?
                syncRelationships( { variables: { input: {
                    objectType,
                    objectId: learningObjectId,
                    relatedResourceType: 'learning-profiles',
                    relatedResourceIds: formValues[ 'learningProfileIds' ] || []
                } } } ) : Promise.resolve()
        ] );
        return results;
    }

    const render = ( learningObjectLoading, disabled ) => (
        <>
            { !updateBrandsLocation &&
                <Row
                    className="segmentation-row"
                    gutter={ [ 24, 24 ] }
                >
                    <Col md={ 24 }>
                        <FeatureFlag name={ flags.BRANDS }>
                            <Card title="Brands">
                                <Loadable
                                    loading={ brandsLoading }>
                                    <Form.Item
                                        name={ getFormName( 'brandIds' ) }
                                        rules={ [
                                            {
                                                required: true,
                                                message: 'At least one brand is required',
                                            }
                                        ] }
                                    >
                                        <SelectOrTransfer
                                            mode="select"
                                            disabled={ disabled }
                                            dataSource={ brandOptions }
                                            onChange= { handleBrandChange }
                                        />
                                    </Form.Item>
                                </Loadable>
                            </Card>
                        </FeatureFlag>
                    </Col>
                </Row>
            }

            <Row
                className="segmentation-row"
                gutter={ [ 24, 24 ] }
            >
                <Col md={ 24 }>
                    <FeatureFlag name={ flags.SEGMENTATION_CLIENT_MANAGED }>
                        <Card title="Segments">
                            { !learningObjectLoading &&
                                <Form.Item
                                    name={ getFormName( 'segmentIds' ) }
                                    valuePropName="segmentIds"
                                    layout="horizontal"
                                >
                                    <RosterGroups
                                        auth={ authService }
                                        form={ formController }
                                        name='segmentIds'
                                        disabled={ disabled }
                                        handleSelectCallback={ ids => {
                                            // Keep both the internal state and the form value in sync so
                                            // saveSegmentation can read the selection off the form value
                                            // (consistent with every other segmentation field).
                                            setSegmentIds( ids );
                                            // `touched` has to be set explicitly. Every other segmentation field is a
                                            // plain Form.Item child whose onChange marks it touched for us, but this one
                                            // is written with setFields, which leaves touched alone on purpose so
                                            // initializeSegmentation cannot dirty the form. This callback only ever runs
                                            // off a real user selection, so mark it here - otherwise a tab reading
                                            // isFieldsTouched (GenericTab) never reports UNSAVED for segment changes.
                                            formController.setFields( [ { name: getFormName( 'segmentIds' ), value: ids, touched: true } ] );
                                        } }
                                        personId={ authService.getClaims()?.[ 'person_id' ] }
                                        companyId={ authService.getClaims()?.[ 'company_id' ] }
                                        groupAttributes={  groupSegmantationPayload === 'true' || groupSegmantationPayload === true
                                            ? [ { name: 'client-managed', value: true } ]
                                            : undefined
                                        }
                                        rosterEditButton={ rosterEditEnabled }
                                    />
                                </Form.Item>
                            }
                        </Card>
                    </FeatureFlag>
                </Col>
            </Row>

            { ( companyRelSegmentation || productsAllowed || localesAllowed ) &&
            <Row 
                className="segmentation-row" 
                gutter={ [ 24, 24 ] }
            >
                <Col md={ 24 }>
                    <Card title="Segmentation">

                        { updateBrandsLocation &&
                            <FeatureFlag name={ flags.BRANDS }>
                                <Loadable
                                    loading={ brandsLoading }>
                                    <Form.Item 
                                        label="Brands"
                                        name={ getFormName( 'brandIds' ) }
                                        rules={ brandsUserRelationshipRequired ? [
                                            {
                                                required: true,
                                                message: 'At least one brand is required',
                                            }
                                        ] : undefined }
                                    >
                                        <SelectOrTransfer
                                            mode="select"
                                            disabled={ disabled }
                                            dataSource={ brandOptions }
                                            onChange= { handleBrandChange }
                                        />
                                    </Form.Item>
                                </Loadable>
                            </FeatureFlag>
                        }

                        <FeatureFlag name={ flags.SEGMENTATION_COMPANY_RELATIONSHIP }>
                            <Loadable
                                loading={ groupsLoading }>
                                <Form.Item 
                                    label="User Relationship to Company" 
                                    name={ getFormName( 'companyRelationshipIds' ) }
                                    rules={ brandsUserRelationshipRequired ? [
                                        {
                                            required: true,
                                            message: 'Courses are required to be available to at least one of these groups.',
                                        }
                                    ] : undefined }
                                >
                                    <SelectOrTransfer
                                        mode="select"
                                        disabled={ disabled }
                                        dataSource={ relationshipToCompanyOptions }
                                    />
                                </Form.Item>
                            </Loadable>
                        </FeatureFlag>

                        <FeatureFlag name={ flags.SEGMENTATION_PRODUCT }>
                            <Loadable
                                loading={ groupsLoading }>
                                <Form.Item 
                                    label="Products"
                                    name={ getFormName( 'productIds' ) }
                                >
                                    <SelectOrTransfer
                                        mode="select"
                                        disabled={ disabled }
                                        dataSource={ productOptions }
                                    />
                                </Form.Item>
                            </Loadable>
                        </FeatureFlag>

                        <FeatureFlag name={ flags.SEGMENTATION_LEARNING_PROFILES }>
                            <Loadable
                                loading={ groupsLoading }>
                                <Form.Item 
                                    label="Learning Profiles"
                                    name={ getFormName( 'learningProfileIds' ) }
                                >
                                    <SelectOrTransfer
                                        mode="select"
                                        disabled={ disabled }
                                        dataSource={ learningProfileOptions }
                                    />
                                </Form.Item>
                            </Loadable>
                        </FeatureFlag>

                        <FeatureFlag name={ flags.SEGMENTATION_LOCALES }>
                            <Loadable
                                loading={ groupsLoading || localeLoading }>
                                <Form.Item 
                                    label={ authService.isHoya ? 'Customer Locales' : 'Locales' }
                                    name={ getFormName( 'localeIds' ) }
                                >
                                    <SelectOrTransfer
                                        mode="transfer"
                                        selectAll={ true }
                                        disabled={ disabled }
                                        dataSource={ segmentOptions }
                                    />
                                </Form.Item>
                            </Loadable>
                        </FeatureFlag>

                        { authService.isHoya() &&
                        <FeatureFlag name={ flags.SEGMENTATION_LOCALES }>
                            <Loadable
                                loading={ groupsLoading || localeLoading }>
                                <Form.Item 
                                    label={ 'Employee Locales' }
                                    name={ getFormName( 'employeeLocaleIds' ) }
                                >
                                    <SelectOrTransfer
                                        mode="transfer"
                                        selectAll={ true }
                                        disabled={ disabled }
                                        dataSource={ segmentOptions }
                                    />
                                </Form.Item>
                            </Loadable>
                        </FeatureFlag>
                        }
                    </Card>
                </Col>
            </Row>
            }
        </>
    );

    return useMemo( () =>  ( {
        loading,
        error: syncRelationshipsError,
        addInitialValues,
        initializeSegmentation,
        saveSegmentation,
        renderSegmentation: render,
        brandOptions,
        setSegmentIds,
    } ), [ loading,  selectedBrands, segmentIds, syncRelationshipsError, brandOptions ] );
};